import React, { useState } from 'react';
import { createCheckoutSession } from '../stripe-config';
import { supabase } from '../lib/supabaseClient';
import { CreditCard, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { trackEvent } from '../services/analyticsService';

interface CheckoutOptionsProps {
  priceId: string;
  mode: 'payment' | 'subscription';
  amount: number;
  productName: string;
  successUrl?: string;
  cancelUrl?: string;
  metadata?: Record<string, string>;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CheckoutOptions: React.FC<CheckoutOptionsProps> = ({
  priceId,
  mode,
  amount,
  productName,
  successUrl,
  cancelUrl,
  metadata = {},
  onSuccess,
  onCancel
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = async () => {
    // Prevent double-click
    if (isProcessing) return;

    setIsProcessing(true);
    setError(null);

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      const userId = session.data.session?.user?.id;

      if (!accessToken) {
        throw new Error('User not authenticated');
      }

      // Track checkout attempt
      const eventType = mode === 'subscription' ? 'subscription_attempt' : 'topup_attempt';
      await trackEvent(eventType, {
        price_id: priceId,
        amount: amount,
        product_name: productName,
        ...metadata
      }, userId);

      const defaultSuccessUrl = `${window.location.origin}/${mode === 'subscription' ? 'subscription' : 'payment'}-success`;
      const defaultCancelUrl = `${window.location.origin}/${mode === 'subscription' ? 'subscription' : 'payment'}-cancelled`;

      const { url } = await createCheckoutSession(
        priceId,
        mode,
        successUrl || defaultSuccessUrl,
        cancelUrl || defaultCancelUrl,
        undefined,
        accessToken,
        metadata
      );

      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');

      // Track checkout failure
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user?.id;
      const eventType = mode === 'subscription' ? 'subscription_failed' : 'topup_failed';
      await trackEvent(eventType, {
        error: err instanceof Error ? err.message : 'Unknown error',
        price_id: priceId,
        amount: amount
      }, userId);

      if (onCancel) onCancel();
    } finally {
      // Always reset processing state after a small delay
      // This handles cases where redirect fails or is blocked
      setTimeout(() => setIsProcessing(false), 1000);
    }
  };

  return (
    <div className="bg-theme-cards rounded-2xl shadow-card overflow-hidden border border-theme-cards p-6">
      {error && (
        <div className="mb-6 bg-secondary/10 p-4 rounded-md flex items-start">
          <AlertCircle size={20} className="mr-3 mt-0.5 text-secondary flex-shrink-0" />
          <div>
            <p className="font-medium text-secondary">Payment Error</p>
            <p className="text-secondary/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-primary-100 p-4 rounded-md mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-contrast">{productName}</span>
          <span className="text-lg font-semibold text-contrast">£{amount.toFixed(2)}</span>
        </div>
        <p className="text-sm text-contrast-400">
          {mode === 'subscription'
            ? 'Recurring payment - cancel anytime'
            : 'One-time payment'}
        </p>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleCheckout}
        disabled={isProcessing}
        className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white py-3 rounded-full hover:from-red-600 hover:to-pink-700 transition-colors flex items-center justify-center disabled:opacity-50 font-semibold"
      >
        {isProcessing ? (
          <span className="flex items-center">
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            Processing...
          </span>
        ) : (
          <>
            <CreditCard size={18} className="mr-2" />
            Proceed to Checkout
          </>
        )}
      </motion.button>

      <p className="text-xs text-contrast-500 text-center mt-4">
        Secure payment processing by Stripe
      </p>
    </div>
  );
};

export default CheckoutOptions;